import React from "react";
import { useForm, validators } from "@hooks";
import classNames from "classnames";
import currency from "currency.js";
import { Constants, helpers, Enums } from "@shared";
import PropTypes from 'prop-types';
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext } from '@contexts';

const UpsertPayor = ({ sunboundTotal, upsert, payor, accountPayors, cancelEditing, limits }) => {

    const { setSpinnerShow } = useSpinnerContext();
    const templatePayor = {
        idPayor: null,
        email: ``,
        fullName: ``,
        payAmount: ``,
        payPercentage: ``,
        dueDay: null,
        amountType: Enums.AmountType.None,
        alreadySelected: false,
        paid: 0
    };

    const validateAmount = (val, vals) => {

        if (sunboundTotal === null) {
            return validators.hasAmount(val) || validators.hasPercentage(vals.payPercentage);
        }

        const amount = currency(val),
            paid = currency(vals.paid),
            min = currency(limits.min),
            max = currency(limits.max);

        if (amount.value < paid.value) {
            return { "payAmount": `Payor has already paid ${paid.format()}` };
        }

        if (min.value !== 0 && amount.value > max.value) {
            return { "payAmount": `There are already payments ${min.format()}. Max amount is ${max.format()}` };
        }

        if (amount.value > max.value) {
            return { "payAmount": `Max payment amount is ${max.format()}` };
        }

        if (amount.value < Constants.System.MIN_PAYMENT_AMOUNT) {
            return { "payAmount": `Min payment amount is ${min.format()}` };
        }

        if (max.subtract(amount).intValue !== 0 && max.subtract(amount).value < currency(Constants.System.MIN_PAYMENT_AMOUNT).value) {
            return { "payAmount": `Guarantor payment is less than ${currency(Constants.System.MIN_PAYMENT_AMOUNT).format()}` };
        }

        return true;
    };

    const validatePercentage = (val, vals) => {
        if (sunboundTotal === null) {
            return validators.hasPercentage(val) || validators.hasAmount(vals.payAmount);
        }
        const re = /^\d{1,3}(\.\d{1,2})?$/;
        if (!re.test(val)) {
            return false;
        }
        const amount = currency(val);
        return amount.value >= 1 && amount.value <= 100;
    };

    const validations = {
        email: (val) => validators.isValidEmail(val) || { email: `Email is not valid` },
        fullName: (val) => validators.hasValue(val) || { fullName: `Full name is required` },
        payPercentage: (val, vals) => validatePercentage(val, vals) || { payPercentage: `Invalid percentage` },
        payAmount: (val, vals) => validateAmount(val, vals) || { payAmount: `Invalid amount` }
    };

    const handleSubmit = async (values) => {
        const newExists = payor === null && accountPayors.find(ap => ap?.payor?.email?.toUpperCase() === values.email.toUpperCase());
        if (newExists) {
            setCustomError(`email`, `Email is already registered`);
            return;
        }

        if (isNewPayor) {
            setSpinnerShow(true);
            try {

                const payorCheck = await sunboundPayProxy.getPayorCheck(values.email);
                if (payorCheck.idPayor !== null && payorCheck.idPayor !== payor.idPayor) {
                    setCustomError(`email`, `Email is already registered`);
                    return;
                }
            } catch (error) {
                console.error(error);
            } finally {
                setSpinnerShow(false);
            }
        }
        upsert({ ...values, payPercentage: currency(values.payPercentage, { precision: 4 }).divide(100).value });
    };

    const { values, errors, changeHandler, submitHandler, customUpdate, setCustomError } = useForm(payor != null ? { ...payor, payAmount: payor.payAmount ?? ``, payPercentage: helpers.printPercentage(payor.payPercentage) } : { ...templatePayor }, {}, validations, handleSubmit);

    const changeAmountProxy = ({ target: { value, name } }) => {

        let payAmount = ``,
            payPercentage = ``,
            amountType = Enums.AmountType.None;
        const re = /^[\d|\\.]+/;
        if (value !== `` && !re.test(value)) {
            return;
        }
        if (value) {
            if (name === `payAmount`) {
                payAmount = value;
                if (sunboundTotal != null) {
                    payPercentage = helpers.printPercentage(helpers.toFixedDown(helpers.percentage(value, sunboundTotal), 4));
                }
                amountType = Enums.AmountType.Amount;
            } else {

                payPercentage = value;
                if (sunboundTotal != null) {
                    payAmount = helpers.toFixedDown(helpers.amount(value, sunboundTotal), 2);
                }
                amountType = Enums.AmountType.Percentage;
            }
        }
        customUpdate({ payAmount, payPercentage, amountType });
    };

    const amountTypeChangeHandler = ({ target: { value } }) => {

        const { amountType } = values;

        if (amountType === Enums.AmountType.None)
            return;

        customUpdate({ amountType: value === `payPercentage` ? Enums.AmountType.Percentage : Enums.AmountType.Amount });
    };

    const days = [];
    for (let i = 1; i <= 25; i++) {
        days.push(<option key={`${i}day`} value={i}>On the {helpers.dayOrdinal(i)}</option>);
    }

    const isNewPayor = values.idPayor === null;

    const title = isNewPayor ? `Create new payor` : values.alreadySelected ? `Update payor` : `Add payor`;

    return (
        <form onSubmit={(e) => submitHandler(e)} className="w-full">
            <div className="content-box border-2 shadow-sb bg-white gap-y-4">
                <h1 className="text-center text-gray-600 text-2xl">{title}</h1>
                <div className="w-full">
                    <label htmlFor="email" className="font-semibold text-gray-700">Email</label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                        <input type="text" name="email" id="email" className={classNames(
                            `block w-full rounded-md shadow-md focus:outline-none border-gray-300 text-gray-600 disabled:bg-gray-200`,
                            { "input-error": errors.email }
                        )} value={values.email ?? ``} onChange={changeHandler} disabled={!isNewPayor} aria-invalid="true" aria-describedby="email-error" />
                        {
                            errors.email &&
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ExclamationCircleIcon className="fill-red-500 h-6" />
                            </div>
                        }
                    </div>
                    {
                        errors.email &&
                        <p className="mt-2 text-sm text-red-600" id="email-error">{errors.email}</p>
                    }
                </div>
                <div className="w-full">
                    <label htmlFor="fullName" className="font-semibold text-gray-700">Full Name</label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                        <input type="text" name="fullName" id="fullName" className={classNames(
                            `block w-full rounded-md shadow-md focus:outline-none border-gray-300 text-gray-600`,
                            { "input-error": errors.fullName }
                        )} value={values.fullName} onChange={changeHandler} aria-invalid="true" aria-describedby="email-error" />
                        {
                            errors.fullName &&
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ExclamationCircleIcon className="fill-red-500 h-6" />
                            </div>
                        }
                    </div>
                    {
                        errors.fullName &&
                        <p className="mt-2 text-sm text-red-600" id="email-error">{errors.fullName}</p>
                    }
                </div>
                <div className="flex flex-row gap-x-6 w-full">
                    <div className="w-full">
                        <input className="form-check-input disabled:bg-gray-400" type="radio" value="payPercentage" disabled={values.amountType === Enums.AmountType.None} checked={values.amountType === Enums.AmountType.Percentage} onChange={amountTypeChangeHandler} name="amountType" id="amountType1" />
                        <label htmlFor="amountType1" className="ml-2 font-semibold text-gray-700">Percentage (%)</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <input type="text" name="payPercentage" id="payPercentage" className={classNames(
                                `block w-full rounded-md shadow-md border-gray-300 focus:outline-none text-gray-600`,
                                { "input-error": errors.payPercentage }
                            )} value={values.payPercentage} onChange={changeAmountProxy} aria-invalid="true" aria-describedby="email-error" />
                            {
                                errors.payPercentage &&
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ExclamationCircleIcon className="fill-red-500 h-6" />
                                </div>
                            }
                        </div>
                        {
                            errors.payPercentage &&
                            <p className="mt-2 text-sm text-red-600" id="email-error">{errors.payPercentage}</p>
                        }
                    </div>

                    <div className="w-full">
                        <input className="form-check-input disabled:bg-gray-400" type="radio" value="payAmount" disabled={values.amountType === Enums.AmountType.None} checked={values.amountType === Enums.AmountType.Amount} onChange={amountTypeChangeHandler} name="amountType" id="amountType21" />
                        <label htmlFor="payAmount" className="ml-2 font-semibold text-gray-700">Amount ($)</label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                            <input type="text" name="payAmount" id="payAmount" maxLength="8" className={classNames(
                                `block w-full rounded-md shadow-md border-gray-300 focus:outline-none text-gray-600`,
                                { "input-error": errors.payAmount }
                            )} value={values.payAmount} onChange={changeAmountProxy} aria-invalid="true" aria-describedby="email-error" />
                            {
                                errors.payAmount &&
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                    <ExclamationCircleIcon className="fill-red-500 h-6" />
                                </div>
                            }
                        </div>
                        {
                            errors.payAmount &&
                            <p className="mt-2 text-sm text-red-600" id="email-error">{errors.payAmount}</p>
                        }
                    </div>

                </div>
                <div className="flex flex-row w-full gap-x-5 justify-center mt-3">
                    <button className="btn btn-danger rounded-full text-lg" type="button" onClick={cancelEditing}>Cancel</button>
                    <button className="btn btn-primary " type="submit">{title}</button>
                </div>
            </div>
        </form >
    );
};

UpsertPayor.propTypes = {
    sunboundTotal: PropTypes.number,
    upsert: PropTypes.func.isRequired,
    cancelEditing: PropTypes.func.isRequired,
    payor: PropTypes.object,
    accountPayors: PropTypes.array.isRequired,
    limits: PropTypes.object
};

export default UpsertPayor;
